import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { OrderExecutionAction } from '@/types/rich-content'
import type { VoiceConfirmationRequired } from '../lib/voice/confirmation-handshake'

/** Local-only origin captured by the callback of the request that produced this token. */
export type PendingCriticalConfirmation = VoiceConfirmationRequired & {
  /** Bound from the response transport, never from a model action argument. */
  sourceConversationId?: string
  replay?: { conversationId: string; orderExecutionAction: OrderExecutionAction }
}

/**
 * Voice status — reduced from 16 states to the 5 the push-to-talk flow really
 * uses (spec comando-de-voz 26/10/2026):
 *
 *   idle -> listening -> sending -> idle
 *                    \-> error / permissionDenied (retry via listening)
 *
 * `transition()` is the single guarded reducer; legal edges live in
 * {@link TRANSITIONS}. Whether the mic EXISTS at all is not decided here: that
 * is the release key `comando_de_voz` (releaseKeysStore).
 */
export type VoiceStatus = 'idle' | 'listening' | 'sending' | 'error' | 'permissionDenied'

/**
 * Legal transitions. An attempt to move to a target not in the source's list is
 * a no-op (illegal jumps are ignored). `error` and `permissionDenied` can always
 * be retried (-> listening), so the FSM is never trapped.
 */
const TRANSITIONS: Record<VoiceStatus, VoiceStatus[]> = {
  idle: ['listening'],
  listening: ['sending', 'error', 'permissionDenied', 'idle'],
  sending: ['idle', 'error', 'listening'],
  error: ['listening', 'idle'],
  permissionDenied: ['listening', 'idle'],
}

/** Patch applied alongside a transition. */
interface VoiceTransitionPatch {
  transcript?: string
  error?: string | null
}

/** Persisted user preferences (survive reload). */
interface VoicePrefs {
  /**
   * Send the final transcript automatically (default ON). OFF fills the box so
   * the user can edit before sending.
   */
  autoSend: boolean
}

/** Ephemeral session state (NOT persisted — resets every reload). */
interface VoiceSession {
  status: VoiceStatus
  transcript: string
  error: string | null
  /**
   * Narration (TTS) of assistant replies. Fixed OFF and out of the voice-command
   * path in this delivery (spec 26/10/2026); never persisted, so no stale
   * preference can switch it on.
   */
  ttsEnabled: boolean
  /**
   * PR8.5 Gap B — parked confirmation handshake from the engine's
   * critical-confirmation gate (`complete_work_order` + the 5 `delete_*`
   * tools, dormant until `CRITICAL_ACTION_CONFIRM_ENFORCE` is on). Set by
   * connectionStore.onResponse when `richContent.voiceConfirmation` is
   * present and parses cleanly; cleared there when a later turn omits it
   * (anti-leak), by chatStore.setActiveConversation on conversation switch,
   * and by the consumer on confirm/cancel/expire.
   *
   * NAMING DEBT (accepted deliberately, spec 2026-07-11 §3): despite the
   * "voice" name, this field is now the shared parking spot for BOTH the
   * voice replay path AND the chat channel (`useCriticalConfirmation` /
   * `CriticalConfirmationCard`). It stays under this name — and the wire key
   * stays `voiceConfirmation` — because renaming it would also mean touching
   * the engine's wire schema, which is out of scope here and would reopen
   * PR #903. Chat call sites must go through the hook's neutral accessor
   * (`pending`), never read this field directly by its "voice" name. A
   * cosmetic rename is a follow-up once the gate itself is live.
   * Ephemeral: NOT persisted. Cleared by resetSession or clearPendingConfirmation.
   */
  pendingConfirmation: PendingCriticalConfirmation | null
}

interface VoiceActions {
  setAutoSend: (autoSend: boolean) => void
  setTranscript: (transcript: string) => void
  appendTranscript: (chunk: string) => void
  /** Park a voice-confirmation handshake received from the engine (PR8.5 Gap B). */
  setPendingConfirmation: (c: PendingCriticalConfirmation) => void
  /** Clear the parked handshake (after PR10 replays or on timeout). */
  clearPendingConfirmation: () => void
  /**
   * Set the error message and move to `error`. Pass `null` as `errorState` to
   * leave the status untouched (e.g. a synthesis failure that is not a
   * recognition failure).
   */
  setError: (error: string | null, errorState?: 'error' | null) => void
  /** Guarded FSM reducer — illegal targets are ignored (no-op + DEV warn). */
  transition: (to: VoiceStatus, patch?: VoiceTransitionPatch) => void
  /** Clear ephemeral session state only; prefs survive. */
  resetSession: () => void
}

type VoiceState = VoicePrefs & VoiceSession & VoiceActions

const EPHEMERAL_DEFAULTS: VoiceSession = {
  status: 'idle',
  transcript: '',
  error: null,
  ttsEnabled: false,
  pendingConfirmation: null,
}

export const useVoiceStore = create<VoiceState>()(
  persist(
    (set, get) => ({
      // Persisted prefs. The mic's existence is decided by the release key
      // `comando_de_voz`, not by a local switch (no forced-off, no `enabled`).
      autoSend: true,
      // Ephemeral session.
      ...EPHEMERAL_DEFAULTS,

      setAutoSend: (autoSend) => set({ autoSend }),

      setTranscript: (transcript) => set({ transcript }),
      appendTranscript: (chunk) => set((state) => ({ transcript: state.transcript + chunk })),

      setPendingConfirmation: (c) => set({ pendingConfirmation: c }),
      clearPendingConfirmation: () => set({ pendingConfirmation: null }),

      setError: (error, errorState = 'error') =>
        set((state) => ({ error, status: error && errorState ? errorState : state.status })),

      transition: (to, patch) => {
        const from = get().status
        if (!TRANSITIONS[from].includes(to)) {
          if (import.meta.env.DEV) {
            console.warn(`[voice] illegal transition ${from} -> ${to} (ignored)`)
          }
          return
        }
        set({ status: to, ...patch })
      },

      resetSession: () => set({ ...EPHEMERAL_DEFAULTS }),
    }),
    {
      name: 'axiomgo-voice-storage',
      // v2 (26/10/2026): the old v1 payload carried `enabled:false`,
      // `autoSend:false` (the old default) and `mode`/`ttsEnabled`. Dropping it
      // lets the new defaults (autoSend ON, narration OFF) apply to everyone.
      version: 2,
      migrate: () => ({}),
      partialize: (state) => ({ autoSend: state.autoSend }),
      merge: (persisted, current) => ({
        ...current,
        ...(typeof (persisted as { autoSend?: unknown } | undefined)?.autoSend === 'boolean'
          ? { autoSend: (persisted as { autoSend: boolean }).autoSend }
          : {}),
      }),
    },
  ),
)
